/**
 * UndoRedoKeyboardHandler - Maneja shortcuts de teclado para Undo/Redo
 *
 * @version 1.0
 * @updated 2026-02-19
 * @module core/map-tools
 *
 * @description
 * Componente invisible que registra listeners globales para:
 * - Ctrl+Z (Cmd+Z en Mac): Deshacer
 * - Ctrl+Y (Cmd+Y en Mac): Rehacer
 * - Ctrl+Shift+Z (Cmd+Shift+Z en Mac): Rehacer (alternativo)
 *
 * @remarks
 * Debe montarse en el root de la aplicación o en MapLayout.
 * Solo procesa shortcuts cuando el usuario NO está editando texto (input/textarea).
 *
 * @example
 * ```tsx
 * import { UndoRedoKeyboardHandler } from '@/core/map-tools';
 *
 * function MapLayout() {
 *   return (
 *     <div>
 *       <UndoRedoKeyboardHandler />
 *       {/* ... resto del layout ... *\/}
 *     </div>
 *   );
 * }
 * ```
 */

import { useEffect } from 'react';
import { useUndoRedo } from './useUndoRedo';

/**
 * Props del componente
 */
export interface UndoRedoKeyboardHandlerProps {
  /**
   * Habilita/deshabilita los shortcuts de teclado
   * @default true
   */
  enabled?: boolean;

  /**
   * Modo verbose (logs en consola)
   * @default false
   */
  verbose?: boolean;
}

/**
 * Componente UndoRedoKeyboardHandler
 */
export function UndoRedoKeyboardHandler({
  enabled = true,
  verbose = false,
}: UndoRedoKeyboardHandlerProps = {}): null {
  const { undo, redo, canUndo, canRedo } = useUndoRedo();

  useEffect(() => {
    if (!enabled) return;

    /**
     * Verifica si el usuario está editando texto
     */
    const isEditingText = (): boolean => {
      const activeElement = document.activeElement;
      if (!activeElement) return false;

      const tagName = activeElement.tagName.toLowerCase();
      const isInput = tagName === 'input' || tagName === 'textarea';
      const isContentEditable = activeElement.hasAttribute('contenteditable');

      return isInput || isContentEditable;
    };

    /**
     * Handler de teclado
     */
    const handleKeyDown = (e: KeyboardEvent): void => {
      // Ignorar si el usuario está editando texto
      if (isEditingText()) {
        return;
      }

      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const ctrlKey = isMac ? e.metaKey : e.ctrlKey;

      // Ctrl+Z (Cmd+Z): Undo
      if (ctrlKey && e.key === 'z' && !e.shiftKey) {
        if (canUndo) {
          e.preventDefault();
          undo();
          if (verbose) {
            console.log('[UndoRedoKeyboardHandler] Undo triggered via keyboard');
          }
        }
        return;
      }

      // Ctrl+Y (Cmd+Y) o Ctrl+Shift+Z (Cmd+Shift+Z): Redo
      if (
        (ctrlKey && e.key === 'y') ||
        (ctrlKey && e.shiftKey && e.key === 'z')
      ) {
        if (canRedo) {
          e.preventDefault();
          redo();
          if (verbose) {
            console.log('[UndoRedoKeyboardHandler] Redo triggered via keyboard');
          }
        }
        return;
      }
    };

    // Registrar listener
    document.addEventListener('keydown', handleKeyDown);

    if (verbose) {
      console.log('[UndoRedoKeyboardHandler] Keyboard shortcuts registered');
    }

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (verbose) {
        console.log('[UndoRedoKeyboardHandler] Keyboard shortcuts unregistered');
      }
    };
  }, [enabled, verbose, undo, redo, canUndo, canRedo]);

  // Componente invisible (no renderiza nada)
  return null;
}
